import { PLATFORM_IS_NATIVE } from "src/hybrid/components/Platform";
import { diagnosticsSessionId } from "src/lib/diagnosticsSession";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { tracker } from "src/state/state";
import type { DeviceInfo, DeviceTrackingState } from "./types";
import { collectDeviceInfo } from "./utils/collectDeviceInfo";
import { collectWebInfo } from "./utils/collectWebInfo";
import { Cubit } from "blac-next";

/**
 * DeviceTrackingBloc
 *
 * Manages device information collection for debugging and analytics.
 * Collects comprehensive device properties once at app startup and sends
 * to backend analytics endpoint.
 */
export class DeviceTrackingBloc extends Cubit<DeviceTrackingState> {
  enabled = true; //PLATFORM_IS_NATIVE;

  constructor() {
    super({
      collected: false,
      deviceInfo: null,
      collectionTime: null,
      error: null
    });
  }

  /**
   * Collects device information and sends to backend analytics
   * Should be called once during app initialization
   * This method is idempotent - subsequent calls are no-ops
   */
  async collectAndTrack(): Promise<void> {
    // Don't collect twice
    if (this.state.collected) {
      return;
    }
    const startTime = Date.now();

    try {
      const deviceInfo = PLATFORM_IS_NATIVE
        ? await collectDeviceInfo()
        : await collectWebInfo();

      const collectionTime = Date.now() - startTime;

      // Update state with collected info
      this.emit({
        collected: true,
        deviceInfo,
        collectionTime,
        error: null
      });

      this.setDeviceInfo(deviceInfo);
    } catch (error) {
      // Report error but mark as collected (attempted)
      reportErrorSentry(error);
      this.emit({
        ...this.state,
        collected: true,
        error: error as Error
      });
    }
  }

  private setDeviceInfo(deviceInfo: DeviceInfo): void {
    // Convert device info to Dict format (null -> undefined)
    // Adds prefix "device_" to keys automatically
    const data = Object.fromEntries(
      Object.entries(deviceInfo).map(([key, value]) => [
        `device_${key}`,
        value === null ? undefined : value
      ])
    );
    data.sessionId = diagnosticsSessionId;
    tracker.setDeviceInfo(data);
  }
}
